import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getEntities,
  getEntity,
  addEntity,
  editEntity,
  deleteEntities,
  activeEntity,
  inactiveEntity,
  startLoadingEntities,
  stopLoadingEntities,
  getEntityHistory,
} from 'app/state/ducks/entities/actions'

import { EntityParams } from 'app/models/entity-params'

/**
 * What a command on an existing entity sends: its id. A whole entity has it,
 * and so does a row of the Entity Management grid, whose row menu and bulk
 * actions send theirs.
 */
type EntityRef = Pick<EntityParams, 'id'>
import { successFailureResponse } from 'app/utils/util-functions'
import {
  coalesceThunk,
  coalesceThunkByKey,
} from 'app/state/utils/coalesce-thunk'
import { LOAD_FAILED, failed } from 'app/state/utils/load-result'

export const saveEntity =
  (savedEntity: EntityParams, entity?: EntityParams) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower = savedEntity.status.toLowerCase() || 'inactive'
    if (!savedEntity.entityParty) savedEntity.entityParty = entity?.entityParty

    if (entity) {
      return client
        .put(`${apiServerUrl}/api/${statusLower}-entities`, savedEntity)
        .then(entity => {
          entity.data.status = 'Inactive'
          const payload = dispatch(editEntity(entity))
          return { success: true, payload: payload.payload }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    }
    delete savedEntity.id
    return client
      .post(`${apiServerUrl}/api/${statusLower}-entities`, savedEntity)
      .then(entity => {
        entity.data.status = statusLower
        const payload = dispatch(addEntity(entity))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

const loadInactiveEntities = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/inactive-entities`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

const loadActiveEntities = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/active-entities`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

export const loadEntities = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingEntities())
      const [inactiveEntities, activeEntities] = await Promise.all([
        loadInactiveEntities(),
        loadActiveEntities(),
      ])
      if ([inactiveEntities, activeEntities].some(failed)) return LOAD_FAILED

      const allEntities = inactiveEntities
        .map((entity: any) => ({ ...entity, status: 'Inactive' }))
        .concat(
          activeEntities.map((entity: any) => ({
            ...entity,
            status: 'Active',
          })),
        )

      return dispatch(getEntities(allEntities))
    } catch (e) {
      console.log(e)
      return LOAD_FAILED
    } finally {
      dispatch(stopLoadingEntities())
    }
  },
)

export const loadOneEntity = coalesceThunkByKey(
  (entityId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower = status.toLowerCase()
    return client
      .get(
        `${apiServerUrl}/api/${statusLower}-entities/one?id=${entityId}`,
      )
      .then(entity => {
        return dispatch(getEntity({ ...entity.data, status: status }))
      })
      .catch(e => {
        return { payload: e.response.data.error, success: false }
      })
  },
)

export const loadEntitySnapshot = (entityId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(`${apiServerUrl}/api/active-entities/snapshot?id=${entityId}`)
    .then(entities => {
      return { success: true, payload: entities.data }
    })
    .catch(e => {
      return {
        success: false,
        payload: e.response?.data?.error || e.message || 'Unknown error',
      }
    })
}

export const removeEntities = (entity: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .delete(`${apiServerUrl}/api/inactive-entities?ids[]=${entity}`)
    .then(entities => {
      return dispatch(deleteEntities(entities.data))
    })
    .catch(e => {
      return { payload: e.response.data.error, success: false }
    })
}

export const activateEntity =
  (entity: EntityRef) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/inactive-entities/activate`, { id: entity.id })
      .then(response => {
        const entityData = (response.data as any).extraData
        dispatch(activeEntity(entityData))
        return { success: true, data: response.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const deactivateEntity =
  (entity: EntityRef) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/active-entities/deactivate`, { id: entity.id })
      .then(response => {
        dispatch(inactiveEntity(response.data))
        return { success: true }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const submitBulkEntities = (entities: EntityParams[]) => async () => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/inactive-entities/bulk`, {
      entityList: entities,
    })
    .then(response => {
      console.log('reponse', response)
      const goodResp = successFailureResponse(response.data)
      console.log('goodResp', goodResp)
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('entities bulk upload error', e)
      return { success: false, payload: e.response.data.error }
    })
}

export const activateBulkEntities = (entities: EntityRef[]) => async () => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/inactive-entities/bulk_activate`, {
      entityList: entities,
    })
    .then(response => {
      console.log('reponse', response)
      const goodResp = successFailureResponse(response.data)
      console.log('goodResp', goodResp)
      return { success: true, payload: response.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const deactivateBulkEntities =
  (entities: EntityRef[]) => async () => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/active-entities/bulk_deactivate`, {
        entityList: entities,
      })
      .then(response => {
        console.log('reponse', response)
        const goodResp = successFailureResponse(response.data)
        console.log('goodResp', goodResp)
        return { success: true, payload: response.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const loadEntityHistory = coalesceThunkByKey(
  (entityId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower = status.toLowerCase()
    return client
      .get(`${apiServerUrl}/api/${statusLower}-entities/history?id=${entityId}`)
      .then(response => {
        return dispatch(getEntityHistory(response.data))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)
